/** @jsxImportSource @emotion/react */
import { Typography } from '@mui/material';
import { useState } from 'react';

import { TertiaryButton } from '@endure/ui';
import type { Token } from 'types';
import { getTokenDisplaySymbol } from 'utilities';
import { Icon } from '../Icon';
import { TokenIcon } from '../TokenIcon';
import { type OptionalTokenBalance, TokenListWrapper } from '../TokenListWrapper';
import { TokenTextField, type TokenTextFieldProps } from '../TokenTextField';
import { useStyles } from './styles';
import {
  getTokenMaxButtonTestId,
  getTokenSelectButtonTestId,
  getTokenTextFieldTestId,
} from './testIdGetters';

export interface SelectTokenTextFieldProps extends Omit<TokenTextFieldProps, 'max' | 'token'> {
  tokenBalances: OptionalTokenBalance[];
  selectedToken: Token;
  onChangeSelectedToken: (token: Token) => void;
  token?: Token;
  displayCommonTokenButtons?: boolean;
  displayTokenSourceLabels?: boolean;
  'data-testid'?: string;
}

const getTokenSourceLabel = (token: Token) => (token.isNative ? 'Native' : 'Wrapped');

const getTokenSourceShortLabel = (token: Token) => (token.isNative ? 'N' : 'W');

const shouldDisplayTokenSourceBadge = (token: Token) =>
  getTokenDisplaySymbol(token) !== token.symbol;

export const SelectTokenTextField: React.FC<SelectTokenTextFieldProps> = ({
  selectedToken,
  disabled,
  tokenBalances,
  onChangeSelectedToken,
  className,
  value,
  rightMaxButton,
  'data-testid': testId,
  description,
  displayCommonTokenButtons = false,
  displayTokenSourceLabels = false,
  ...otherTokenTextFieldProps
}) => {
  const styles = useStyles();
  const [isTokenListShown, setIsTokenListShown] = useState(false);

  const handleButtonClick = () => setIsTokenListShown((isShowing) => !isShowing);

  return (
    <div className={className} data-testid={testId}>
      <TokenListWrapper
        onTokenClick={onChangeSelectedToken}
        tokenBalances={tokenBalances}
        onClose={() => setIsTokenListShown(false)}
        isListShown={isTokenListShown}
        selectedToken={selectedToken}
        displayTokenSourceLabels={displayTokenSourceLabels}
        data-testid={testId}
      >
        <TokenTextField
          token={selectedToken}
          disabled={disabled}
          value={value}
          displayTokenIcon={false}
          leftAdornment={
            <TertiaryButton
              onClick={handleButtonClick}
              css={styles.getButton({ isTokenListShown })}
              className="pl-2 pr-1 max-w-40 min-w-0"
              contentClassName="min-w-0"
              variant="tertiary"
              disabled={disabled}
              data-testid={!!testId && getTokenSelectButtonTestId({ parentTestId: testId })}
              size="sm"
            >
              <div className="flex min-w-0 grow items-center gap-x-1.5 overflow-hidden">
                <TokenIcon token={selectedToken} className="h-5 w-5 shrink-0" />

                <div className="min-w-0 grow truncate leading-none">
                  {getTokenDisplaySymbol(selectedToken)}
                </div>

                {displayTokenSourceLabels && shouldDisplayTokenSourceBadge(selectedToken) && (
                  <div
                    className="flex size-4 shrink-0 items-center justify-center rounded-full border border-lightGrey text-[10px] font-semibold leading-none text-light-grey"
                    title={getTokenSourceLabel(selectedToken)}
                  >
                    {getTokenSourceShortLabel(selectedToken)}
                  </div>
                )}
              </div>

              <Icon
                css={styles.getArrowIcon({ isTokenListShown })}
                name="arrowUp"
                className="w-5 h-5 ml-1 shrink-0"
              />
            </TertiaryButton>
          }
          rightAdornment={
            rightMaxButton && (
              <TertiaryButton
                disabled={disabled}
                css={styles.maxButton}
                data-testid={!!testId && getTokenMaxButtonTestId({ parentTestId: testId })}
                size="sm"
                className="px-2"
                {...rightMaxButton}
              >
                {rightMaxButton.label}
              </TertiaryButton>
            )
          }
          data-testid={!!testId && getTokenTextFieldTestId({ parentTestId: testId })}
          {...otherTokenTextFieldProps}
        />
      </TokenListWrapper>

      <Typography variant="small2" css={styles.description}>
        {description}
      </Typography>
    </div>
  );
};
